"use client";

/**
 * `<Reveal>` — scroll-triggered fade-in wrapper.
 *
 * CSS-only implementation: applies the `.reveal` class defined in
 * `src/styles/design-tokens.css` and toggles `.reveal.in` via
 * IntersectionObserver when the element crosses the 20% viewport
 * threshold. Observer disconnects once revealed (one-shot reveal —
 * the same `viewport.once: true` contract the previous Framer
 * Motion version had).
 *
 * Why CSS-only: Framer Motion shipped ~50KB gzipped just to play a
 * single fade animation. The runtime cost across dozens of `<Reveal>`
 * wrappers on a page was a Lighthouse "unused JavaScript" and
 * "main-thread work" tax we couldn't justify for a once-per-mount
 * fade.
 *
 * Usage matches the old API:
 *   <Reveal delay={120}>...</Reveal>
 *   <Reveal as="section" delay={80}>...</Reveal>
 *
 * The `delay` prop applies as inline `transition-delay`, so multiple
 * stacked `<Reveal>`s can still stagger.
 */

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils/cn";

interface RevealProps {
  children: ReactNode;
  delay?: number;
  className?: string;
  style?: CSSProperties;
  as?: "div" | "section" | "article";
}

export function Reveal({
  children,
  delay = 0,
  className,
  style,
  as = "div",
}: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || revealed) return;

    // Respect the user's reduced-motion preference — skip the fade
    // and reveal immediately so screen-readers / vestibular users
    // don't see content delayed.
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setRevealed(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true);
            io.disconnect();
            break;
          }
        }
      },
      // threshold 0 + a generous rootMargin: reveal as soon as any part is
      // near the viewport. Never gate on a % of a tall element (or a
      // zero-height wrapper while a `next/dynamic` child is still loading).
      { threshold: 0, rootMargin: "300px 0px 300px 0px" },
    );
    io.observe(el);
    // Safety net — content must NEVER be stuck invisible. If the observer
    // hasn't fired shortly after mount (fast scroll past, or a lazily
    // mounted child that gains height after the first intersection check),
    // reveal anyway.
    const fallback = window.setTimeout(() => setRevealed(true), 1100);
    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
    };
  }, [revealed]);

  const mergedStyle: CSSProperties = {
    transitionDelay: revealed ? `${delay}ms` : undefined,
    ...style,
  };
  const classNames = cn("reveal", revealed && "in", className);

  if (as === "section") {
    return (
      <section
        ref={ref as React.RefObject<HTMLElement> as React.RefObject<HTMLDivElement>}
        className={classNames}
        style={mergedStyle}
      >
        {children}
      </section>
    );
  }
  if (as === "article") {
    return (
      <article
        ref={ref as React.RefObject<HTMLElement> as React.RefObject<HTMLDivElement>}
        className={classNames}
        style={mergedStyle}
      >
        {children}
      </article>
    );
  }
  return (
    <div ref={ref} className={classNames} style={mergedStyle}>
      {children}
    </div>
  );
}
